import { adminClient, inferAdditionalFields } from "better-auth/client/plugins";
import { createAuthClient } from "better-auth/react";

// Same origin as the panel; the Worker serves /api/auth/*.
export const authClient = createAuthClient({
  baseURL: window.location.origin,
  plugins: [
    adminClient(),
    inferAdditionalFields({ user: { approved: { type: "boolean", required: false, input: false } } }),
  ],
});

type SessionData = NonNullable<Awaited<ReturnType<typeof authClient.getSession>>["data"]>;
export type SessionCheck =
  | { state: "signed-in"; data: SessionData }
  | { state: "signed-out" }
  | { state: "unavailable"; message: string };

// A successful check is reused briefly: pages check before every navigation, and the server enforces the
// session on every request anyway.
const FRESH_MS = 30_000;
let recent: { at: number; data: SessionData } | null = null;

/**
 * "Am I signed in?" that only says no when the server says no. A failed check (network, a 5xx, rate
 * limiting) is retried with backoff and then reported as "unavailable", never as signed out: sending people
 * to the login page on a hiccup is how valid sessions used to look like lockouts.
 */
export async function checkSession(): Promise<SessionCheck> {
  if (recent && Date.now() - recent.at < FRESH_MS) return { state: "signed-in", data: recent.data };
  let last = "";
  for (let attempt = 0; attempt < 3; attempt++) {
    if (attempt) await new Promise((r) => setTimeout(r, 500 * 2 ** (attempt - 1)));
    try {
      const { data, error } = await authClient.getSession();
      if (!error || error.status === 401) {
        if (!error && data) {
          recent = { at: Date.now(), data };
          return { state: "signed-in", data };
        }
        recent = null;
        return { state: "signed-out" };
      }
      last = error.message || `HTTP ${error.status}`;
    } catch (e) {
      last = String(e);
    }
  }
  return { state: "unavailable", message: last };
}

/** Call on sign-out so the next check asks the server. */
export function forgetSession() {
  recent = null;
}
